import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, Percent, DollarSign } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

/**
 * Create/edit dialog for a discount/fixed-price bundle. Purely presentational.
 */
export const BundleFormDialog = ({
  open, onOpenChange,
  editing,
  form, setForm,
  pricingMode, setPricingMode,
  classes,
  saving,
  onSave,
}) => {
  const { t } = useLanguage();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border">
        <DialogHeader>
          <DialogTitle>{editing ? t('bundles.editBundle') : t('bundles.createBundle')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>{t('bundles.bundleName')}</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t('bundles.bundleNamePlaceholder')}
              className="bg-secondary border-border mt-1"
              data-testid="bundle-name-input"
            />
          </div>
          <div>
            <Label>{t('bundles.selectClass')}</Label>
            <Select
              value={form.class_id}
              onValueChange={(v) => setForm({ ...form, class_id: v === 'global' ? '' : v })}
            >
              <SelectTrigger className="bg-secondary border-border mt-1" data-testid="bundle-class-select">
                <SelectValue placeholder={t('bundles.allClasses')} />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="global">{t('bundles.allClasses')}</SelectItem>
                {classes.map((cls) => (
                  <SelectItem key={cls.class_id} value={cls.class_id}>
                    {cls.name} ({cls.year})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t('bundles.pricingMode')}</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              <Button
                type="button"
                variant={pricingMode === 'discount' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPricingMode('discount')}
                className={pricingMode === 'discount' ? 'bg-primary hover:bg-primary/90' : ''}
                data-testid="pricing-mode-discount"
              >
                <Percent className="w-3.5 h-3.5 mr-1.5" />
                {t('bundles.discountPercent')}
              </Button>
              <Button
                type="button"
                variant={pricingMode === 'fixed' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPricingMode('fixed')}
                className={pricingMode === 'fixed' ? 'bg-primary hover:bg-primary/90' : ''}
                data-testid="pricing-mode-fixed"
              >
                <DollarSign className="w-3.5 h-3.5 mr-1.5" />
                {t('bundles.fixedPrice')}
              </Button>
            </div>
          </div>
          {pricingMode === 'discount' ? (
            <div>
              <Label>{t('bundles.discountPercent')}</Label>
              <Input
                type="number" step="1" min="1" max="99"
                value={form.discount_percent}
                onChange={(e) => setForm({ ...form, discount_percent: e.target.value })}
                placeholder={t('bundles.discountPlaceholder')}
                className="bg-secondary border-border mt-1"
                data-testid="bundle-discount-input"
              />
            </div>
          ) : (
            <div>
              <Label>{t('bundles.fixedPrice')} (CHF)</Label>
              <Input
                type="number" step="0.05" min="0.50"
                value={form.fixed_price}
                onChange={(e) => setForm({ ...form, fixed_price: e.target.value })}
                placeholder={t('bundles.fixedPricePlaceholder')}
                className="bg-secondary border-border mt-1"
                data-testid="bundle-fixed-price-input"
              />
            </div>
          )}
          <div className="flex items-center gap-3">
            <Switch
              checked={form.is_active}
              onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              data-testid="bundle-active-switch"
            />
            <Label>{t('prints.isActive')}</Label>
          </div>
        </div>
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onSave} disabled={saving} className="bg-primary hover:bg-primary/90" data-testid="save-bundle-btn">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editing ? t('common.update') : t('common.create')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
